import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROLES, PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { LoadingSpinner } from "../../components/common/Loading";
import { payrollAPI } from "../../services/api/payroll.api";

const PayrollApprovalPage = () => {
  const user = useSelector(selectUser);
  const { hasPermission, hasAnyRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  // State management
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(new Date());
  const [payrollStatus, setPayrollStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Dialog states
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [rejectionComment, setRejectionComment] = useState("");
  const [selectedPayroll, setSelectedPayroll] = useState(null);

  useEffect(() => {
    fetchPayrollForApproval();
    fetchPayrollStatus();
  }, [currentPeriod]);

  const fetchPayrollForApproval = async () => {
    try {
      setLoading(true);
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const response = await payrollAPI.getPayrollStatus({ month, year });

      if (response.data.success) {
        const submittedPayroll =
          response.data.data.payroll_records?.filter(
            (payroll) => payroll.status === "submitted"
          ) || [];
        setPayrollData(submittedPayroll);
      } else {
        throw new Error(
          response.data.message || "Failed to fetch payroll data"
        );
      }
    } catch (error) {
      console.error("Failed to fetch payroll for approval:", error);
      showError("Failed to fetch payroll for approval");
      setPayrollData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollStatus = async () => {
    try {
      setStatusLoading(true);
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const response = await payrollAPI.getPayrollStatus({ month, year });

      if (response.data.success) {
        setPayrollStatus(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch payroll status:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleApprovePayroll = async () => {
    if (!selectedPayroll) return;

    try {
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const approvalData = {
        month,
        year,
        comment: approvalComment.trim(),
      };

      const response = await payrollAPI.approvePayroll(approvalData);

      if (response.data.success) {
        showSuccess("Payroll approved successfully");
        setApprovalDialog(false);
        setApprovalComment("");
        setSelectedPayroll(null);
        await fetchPayrollForApproval();
        await fetchPayrollStatus();
      } else {
        throw new Error(response.data.message || "Failed to approve payroll");
      }
    } catch (error) {
      console.error("Failed to approve payroll:", error);
      showError("Failed to approve payroll");
    }
  };

  const handleRejectPayroll = async () => {
    if (!selectedPayroll || !rejectionComment.trim()) return;

    try {
      const month = currentPeriod.getMonth() + 1;
      const year = currentPeriod.getFullYear();

      const rejectionData = {
        month,
        year,
        comment: rejectionComment.trim(),
      };

      const response = await payrollAPI.rejectPayroll(rejectionData);

      if (response.data.success) {
        showSuccess("Payroll rejected successfully");
        setRejectionDialog(false);
        setRejectionComment("");
        setSelectedPayroll(null);
        await fetchPayrollForApproval();
        await fetchPayrollStatus();
      } else {
        throw new Error(response.data.message || "Failed to reject payroll");
      }
    } catch (error) {
      console.error("Failed to reject payroll:", error);
      showError("Failed to reject payroll");
    }
  };

  const handleDownloadReport = () => {
    const period = format(currentPeriod, "MMMM yyyy");

    // Create CSV content
    const csvHeaders = [
      "Employee Name",
      "Registration Number",
      "Department",
      "Basic Salary",
      "Total Earnings",
      "Total Deductions",
      "Net Pay",
      "Status",
      "Processed By",
      "Approved By",
      "Approval Comment",
      "Rejection Comment",
    ];

    const csvData = payrollData.map((item) => [
      item.employee?.name || "N/A",
      item.employee?.registration_number || "N/A",
      item.employee?.department || "N/A",
      item.base_salary,
      item.total_earnings,
      item.total_deductions,
      item.net_pay,
      item.status,
      item.processed_by || "N/A",
      item.approved_by || "N/A",
      item.approval_comment || "N/A",
      item.rejection_comment || "N/A",
    ]);

    // Add total row
    const totalNetPay = payrollData.reduce(
      (sum, item) => sum + (parseFloat(item.net_pay) || 0),
      0
    );
    const totalEarnings = payrollData.reduce(
      (sum, item) => sum + (parseFloat(item.total_earnings) || 0),
      0
    );
    const totalDeductions = payrollData.reduce(
      (sum, item) => sum + (parseFloat(item.total_deductions) || 0),
      0
    );

    csvData.push([
      "TOTAL",
      "",
      "",
      "",
      totalEarnings,
      totalDeductions,
      totalNetPay,
      "",
      "",
      "",
      "",
      "",
    ]);

    // Convert to CSV string
    const csvContent = [
      csvHeaders.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `payroll_approval_report_${period.replace(" ", "_")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccess(
      `Payroll approval report for ${period} downloaded successfully`
    );
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
    }).format(numAmount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "submitted":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "submitted":
        return "Submitted for Approval";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const canApprove = hasAnyRole([ROLES.ADMIN]) && payrollData.length > 0;
  const canReject = hasAnyRole([ROLES.ADMIN]) && payrollData.length > 0;

  const totalNetPay = payrollData.reduce(
    (sum, item) => sum + (parseFloat(item.net_pay) || 0),
    0
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Payroll Approval
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and approve submitted payroll for the selected period
          </Typography>
        </Box>

        {/* Period Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Pay Period"
                  value={currentPeriod}
                  onChange={setCurrentPeriod}
                  views={["year", "month"]}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchPayrollForApproval}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" color="primary">
                  Total Net Pay: {formatCurrency(totalNetPay)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payroll Status Summary */}
        {payrollStatus && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payroll Status for {format(currentPeriod, "MMMM yyyy")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {payrollStatus.summary?.total_employees || 0}
                    </Typography>
                    <Typography variant="body2">Total Employees</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {payrollStatus.summary?.processed || 0}
                    </Typography>
                    <Typography variant="body2">Processed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {payrollStatus.summary?.submitted || 0}
                    </Typography>
                    <Typography variant="body2">Submitted</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {payrollStatus.summary?.approved || 0}
                    </Typography>
                    <Typography variant="body2">Approved</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error.main">
                      {payrollStatus.summary?.rejected || 0}
                    </Typography>
                    <Typography variant="body2">Rejected</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="info.main">
                      {formatCurrency(
                        payrollStatus.summary?.total_net_pay || 0
                      )}
                    </Typography>
                    <Typography variant="body2">Total Net Pay</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Only payroll submitted for approval by
            Finance Managers will appear here. You can approve with an optional
            comment or reject with a required comment. If no payroll appears,
            check if payroll has been submitted for the selected period.
          </Typography>
        </Alert>

        {/* Payroll Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 3 }}>
                <LoadingSpinner />
              </Box>
            ) : payrollData.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary">
                  No payroll submitted for approval in{" "}
                  {format(currentPeriod, "MMMM yyyy")}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Employee</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Details</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Working Days</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Salary</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Net Pay</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payrollData.map((item) => (
                      <TableRow
                        key={item.id}
                        onClick={() => {
                          setSelectedPayroll(item);
                          setReceiptDialog(true);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "medium" }}
                            >
                              {item.employee?.name || "N/A"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.employee?.registration_number || "N/A"} •{" "}
                              {item.employee?.department || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              <strong>HELSB:</strong>{" "}
                              {item.employee?.helsb || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>NSSF:</strong>{" "}
                              {item.employee?.nssf || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>BIMA:</strong>{" "}
                              {item.employee?.bima || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {item.working_days || 0} days
                            </Typography>
                            <Typography variant="body2">
                              {item.total_hours || 0}h
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(
                            item.base_salary || item.total_earnings || 0
                          )}
                        </TableCell>
                        <TableCell>{formatCurrency(item.net_pay)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(item.status)}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() => {
                                  setSelectedPayroll(item);
                                  setApprovalDialog(true);
                                }}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                  setSelectedPayroll(item);
                                  setRejectionDialog(true);
                                }}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {canApprove && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => {
                    setSelectedPayroll({ id: "all" });
                    setApprovalDialog(true);
                  }}
                  disabled={payrollData.length === 0}
                >
                  Approve All Payroll
                </Button>
              )}

              {canReject && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    setSelectedPayroll({ id: "all" });
                    setRejectionDialog(true);
                  }}
                  disabled={payrollData.length === 0}
                >
                  Reject All Payroll
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadReport}
                disabled={payrollData.length === 0}
              >
                Download Report
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Approval Dialog */}
        <Dialog
          open={approvalDialog}
          onClose={() => setApprovalDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Approve Payroll</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to approve payroll for{" "}
                <strong>{format(currentPeriod, "MMMM yyyy")}</strong>?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Approval Comment (Optional)"
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
            <Button
              onClick={handleApprovePayroll}
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
            >
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog
          open={rejectionDialog}
          onClose={() => setRejectionDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Payroll</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to reject payroll for{" "}
                <strong>{format(currentPeriod, "MMMM yyyy")}</strong>?
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Rejection comment is required.
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Comment (Required)"
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                required
                error={!rejectionComment.trim()}
                helperText={
                  !rejectionComment.trim()
                    ? "Rejection comment is required"
                    : ""
                }
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
            <Button
              onClick={handleRejectPayroll}
              variant="contained"
              color="error"
              startIcon={<RejectIcon />}
              disabled={!rejectionComment.trim()}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payroll Receipt Dialog */}
        <Dialog
          open={receiptDialog}
          onClose={() => setReceiptDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Payroll Receipt</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPayroll && format(currentPeriod, "MMMM yyyy")}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedPayroll && (
              <Box sx={{ pt: 2 }}>
                {/* Header */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 3,
                    pb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    GHF HR Management System
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Payroll Receipt
                  </Typography>
                </Box>

                {/* Employee Details */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {selectedPayroll.employee?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Registration Number
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayroll.employee?.registration_number || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayroll.employee?.department || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Pay Period
                    </Typography>
                    <Typography variant="body1">
                      {format(currentPeriod, "MMMM yyyy")}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Payroll Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Payroll Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Base Salary
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          {formatCurrency(selectedPayroll.base_salary)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Total Earnings
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          {formatCurrency(selectedPayroll.total_earnings)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Total Deductions
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          {formatCurrency(selectedPayroll.total_deductions)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Net Pay
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          {formatCurrency(selectedPayroll.net_pay)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Status and Approval Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Status & Approval Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={getStatusText(selectedPayroll.status)}
                        color={getStatusColor(selectedPayroll.status)}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                    {selectedPayroll.processed_by && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Processed By
                        </Typography>
                        <Typography variant="body1">
                          {selectedPayroll.processed_by}
                        </Typography>
                      </Grid>
                    )}
                    {selectedPayroll.approved_by && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Approved By
                        </Typography>
                        <Typography variant="body1">
                          {selectedPayroll.approved_by}
                        </Typography>
                      </Grid>
                    )}
                    {selectedPayroll.approval_comment && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Approval Comment
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontStyle: "italic" }}
                        >
                          "{selectedPayroll.approval_comment}"
                        </Typography>
                      </Grid>
                    )}
                    {selectedPayroll.rejection_comment && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Rejection Comment
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontStyle: "italic", color: "error.main" }}
                        >
                          "{selectedPayroll.rejection_comment}"
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 4,
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    This is a computer-generated document. No signature
                    required.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generated on {format(new Date(), "PPP")}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReceiptDialog(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                // TODO: Add print functionality
                window.print();
              }}
            >
              Print Receipt
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PayrollApprovalPage;
